import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { Card, cn } from "@tellescope/design-system";
import { useOrganization } from "../../shared/store/organizationStore";

interface AuthPageLayoutProps {
  /** Heading for the card. Pass the organization's override already resolved against the default. */
  title: string;
  /** Optional sub-heading rendered below the title. */
  description?: string;
  /** Status icon rendered above the title, below the logo (e.g. the reset-password confirmations). */
  icon?: LucideIcon;
  iconClassName?: string;
  titleClassName?: string;
  /** The card's body — typically a `Card.Body` and a `Card.Footer`. */
  children: ReactNode;
}

/**
 * Shared shell for every pre-portal (unauthenticated) screen: the full-height background, the
 * fixed-width card, the organization logo, and the title/description header. Using it everywhere
 * is what keeps logo size and card width identical across the whole sign-in journey.
 */
export const AuthPageLayout = ({
  title,
  description,
  icon: Icon,
  iconClassName,
  titleClassName,
  children,
}: AuthPageLayoutProps) => {
  const { t } = useTranslation("auth");
  const organization = useOrganization();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card variant="floating" padding="root" className="w-full max-w-md">
        <Card.Header className="text-center">
          {organization?.logoURL && (
            <div className="flex justify-center mb-4">
              <img
                src={organization.logoURL}
                alt={organization.name || t("Organization logo")}
                className="h-16 max-w-[200px] object-contain"
              />
            </div>
          )}
          {Icon && (
            <div className="flex justify-center mb-4">
              <Icon className={cn("h-12 w-12", iconClassName)} />
            </div>
          )}
          <Card.Title className={cn("text-2xl font-bold", titleClassName)}>{title}</Card.Title>
          {description && <Card.Description>{description}</Card.Description>}
        </Card.Header>

        {children}
      </Card>
    </div>
  );
};
